<!-- src/apps/workspace/account/settings/ProfileSettings.vue -->

<script setup lang="ts">
  import { useI18n } from 'vue-i18n';
  import { useAccount } from '@/shared/composables/useAccount';
  import AccountCard from '@/apps/workspace/components/account/AccountCard.vue';
  import OIcon from '@/shared/components/icons/OIcon.vue';
  import LanguageToggle
    from '@/shared/components/ui/LanguageToggle.vue';
  import SettingsLayout
    from '@/apps/workspace/layouts/SettingsLayout.vue';
  import ThemeToggle
    from '@/shared/components/ui/ThemeToggle.vue';
  import {
    useBootstrapStore,
  } from '@/shared/stores/bootstrapStore';
  import { storeToRefs } from 'pinia';
  import { formatDisplayDate } from '@/utils/format';
  import { isOwnerOrAdminOf } from '@/utils/features';
  import { computed, ref, onMounted } from 'vue';

  const { t } = useI18n();
  const { accountInfo, fetchAccountInfo } = useAccount();

  const bootstrapStore = useBootstrapStore();
  const { i18n_enabled, has_password } = storeToRefs(bootstrapStore);
  const canChangeEmail = computed(() => has_password.value && isOwnerOrAdminOf(bootstrapStore));

  const currentEmail = computed(
    () => bootstrapStore.email
  );

  const emailVerified = computed(
    () => accountInfo.value?.email_verified ?? false
  );

  const accountCreatedDate = computed(() => {
    if (!accountInfo.value?.created_at) return '';
    return formatDisplayDate(new Date(accountInfo.value.created_at));
  });

  const isLoading = ref(false);

  const handleThemeChange = async (
    _isDark: boolean
  ) => {
    isLoading.value = true;
    try {
      // TODO: Persist theme preference to user settings
    } catch (error) {
      console.error('Error changing theme:', error);
    } finally {
      isLoading.value = false;
    }
  };

  onMounted(async () => {
    await fetchAccountInfo();
  });
</script>

<template>
  <SettingsLayout>
    <div class="space-y-6">
      <!-- Email Address -->
      <AccountCard
        :icon="{ collection: 'heroicons', name: 'envelope' }"
        :title="t('web.auth.account.email')">
        <div
          class="flex flex-wrap items-center justify-between gap-4 border-b border-day-line px-[22px] py-[18px] dark:border-white/[0.13]">
          <div>
            <!--
              The mock sets the address itself in the mono face — it is an
              identifier, and the surrounding copy is the sans.
            -->
            <p class="font-mono text-sm text-day-ink dark:text-night-ink">
              {{ currentEmail }}
            </p>
            <div class="mt-2 flex items-center gap-[7px]">
              <!-- SSO users: show linked status -->
              <template v-if="!has_password">
                <OIcon
                  collection="heroicons"
                  name="link-solid"
                  class="size-4 text-brand-600 dark:text-brand-500"
                  aria-hidden="true" />
                <span class="text-[13px] text-brand-600 dark:text-brand-500">
                  {{ t('web.auth.account.sso_linked') }}
                </span>
              </template>
              <!-- Password users: show verification status. The mock's verified
                   mark is a FILLED green disc with a white tick, not an outline
                   glyph — it has to read at a glance next to the address. -->
              <template v-else>
                <span
                  v-if="emailVerified"
                  class="flex size-[17px] shrink-0 items-center justify-center rounded-full bg-emerald-600 dark:bg-emerald-500"
                  aria-hidden="true">
                  <OIcon
                    collection="heroicons"
                    name="check"
                    class="size-2.5 text-white" />
                </span>
                <span
                  :class="[
                    'text-[13px]',
                    emailVerified
                      ? 'text-emerald-600 dark:text-emerald-500'
                      : 'text-day-ink-faint dark:text-night-ink-faint',
                  ]">
                  {{
                    emailVerified
                      ? t('web.auth.account.verified')
                      : t('web.auth.account.not_verified')
                  }}
                </span>
              </template>
            </div>
          </div>
          <router-link
            v-if="canChangeEmail"
            to="/account/settings/profile/email"
            class="inline-flex items-center gap-[7px] text-sm text-brand-600 transition-colors
              hover:text-brand-700 dark:text-brand-500 dark:hover:text-brand-400">
            {{ t('web.settings.profile.change_email') }}
            <OIcon
              collection="heroicons"
              name="arrow-right-solid"
              class="size-[15px]"
              aria-hidden="true" />
          </router-link>
        </div>

        <div
          v-if="accountCreatedDate"
          class="px-[22px] py-4">
          <p class="text-[13px] text-day-ink-faint dark:text-night-ink-faint">
            {{ t('web.auth.account.created') }}
          </p>
          <p class="mt-0.5 font-mono text-[13px] text-day-ink-muted dark:text-night-ink-muted">
            {{ accountCreatedDate }}
          </p>
        </div>
      </AccountCard>

      <!-- Preferences -->
      <AccountCard
        :icon="{ collection: 'heroicons', name: 'adjustments-horizontal-solid' }"
        :title="t('web.settings.preferences')">
        <!-- Theme Setting -->
        <div
          class="flex flex-wrap items-center justify-between gap-4 border-b border-day-line px-[22px] py-4 dark:border-white/[0.13]">
          <div class="flex items-center gap-3">
            <!-- ph-sun: the outline sun heroicons does not ship in this
                 sprite set; Phosphor's is the same 1.5px stroke language. -->
            <OIcon
              collection="ph"
              name="sun"
              class="size-[18px] shrink-0 text-day-ink-muted dark:text-night-ink-muted"
              aria-hidden="true" />
            <div>
              <p class="text-sm text-day-ink dark:text-night-ink">
                {{ t('web.COMMON.appearance') }}
              </p>
              <p class="mt-px text-[13px] text-day-ink-faint dark:text-night-ink-faint">
                {{ t('web.settings.theme.choose_light_or_dark_theme') }}
              </p>
            </div>
          </div>
          <ThemeToggle
            @theme-changed="handleThemeChange"
            :disabled="isLoading"
            :aria-busy="isLoading" />
        </div>

        <!-- Language Setting -->
        <div
          v-if="i18n_enabled"
          class="px-[22px] py-4">
          <div class="flex flex-wrap items-center justify-between gap-4">
            <div class="flex items-center gap-3">
              <OIcon
                collection="heroicons"
                name="language"
                class="size-[18px] shrink-0 text-day-ink-muted dark:text-night-ink-muted"
                aria-hidden="true" />
              <div>
                <p class="text-sm text-day-ink dark:text-night-ink">
                  {{ t('web.COMMON.language') }}
                </p>
                <p class="mt-px text-[13px] text-day-ink-faint dark:text-night-ink-faint">
                  {{ t('web.settings.language.select_your_preferred_language') }}
                </p>
              </div>
            </div>
            <LanguageToggle />
          </div>
        </div>
      </AccountCard>
    </div>
  </SettingsLayout>
</template>
